import assert from '@banking/ui/utils/assert';
import { GetCardListRequest } from '@square/dashboard-protos/models/squareup/bbfrontend/v1/card_list';
import { RetrieveCardRelationshipsRequest, RetrieveCardRelationshipsResponse } from '@square/dashboard-protos/models/squareup/bbdebit/cardmanagement';
import { convertYearMonthDayToDate } from '@banking/ui/utils/datetime-converter';
const INITIAL_STATE = {
  allowMultipleCards: false,
  allowOwnerOrderAdditionalDebitCards: false,
  cards: [],
  canInviteOrOrderNewCard: false,
  doesOwnerHaveNonTerminatedCard: false,
  hasError: false,
  isLoading: true,
  tooltipText: '',
  cardTokenWithReplacementDueDate: null,
  unitToken: ''
};
export const createState = (state = {}) => ({
  ...INITIAL_STATE,
  ...state
});
export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["CardListReceived"] = "CARD_LIST/CARD_LIST_RECEIVED";
  ActionType["CardListRejected"] = "CARD_LIST/CARD_LIST_REJECTED";
  ActionType["CardRelationshipsReceived"] = "CARD_LIST/CARD_RELATIONSHIPS_RECEIVED";
  ActionType["CardRelationshipsRejected"] = "CARD_LIST/CARD_RELATIONSHIPS_REJECTED";
  ActionType["RequestCardList"] = "CARD_LIST/REQUEST_CARD";
  ActionType["RequestCardRelationships"] = "CARD_LIST/REQUEST_CARD_RELATIONSHIPS";
  ActionType["UserClickAddDebitCard"] = "CARD_LIST/USER_CLICK_ADD_DEBIT_CARD";
  return ActionType;
}({});
export let ActionCreator;
(function (_ActionCreator) {
  const cardListReceived = _ActionCreator.cardListReceived = response => ({
    type: ActionType.CardListReceived,
    response
  });
  const cardListRejected = _ActionCreator.cardListRejected = error => ({
    type: ActionType.CardListRejected,
    error
  });
  const cardRelationshipsReceived = _ActionCreator.cardRelationshipsReceived = responsesMap => ({
    type: ActionType.CardRelationshipsReceived,
    responsesMap
  });
  const cardRelationshipsRejected = _ActionCreator.cardRelationshipsRejected = error => ({
    type: ActionType.CardRelationshipsRejected,
    error
  });
  const requestCardList = _ActionCreator.requestCardList = () => ({
    type: ActionType.RequestCardList
  });
  const requestCardRelationships = _ActionCreator.requestCardRelationships = () => ({
    type: ActionType.RequestCardRelationships
  });
  const userClickAddDebitCard = _ActionCreator.userClickAddDebitCard = () => ({
    type: ActionType.UserClickAddDebitCard
  });
})(ActionCreator || (ActionCreator = {}));
const handleCardListReceived = (state, action) => {
  const {
    response
  } = action;
  const {
    cards,
    allowOrderNewCard,
    informationBannerText
  } = response;
  // If there are multiple expiring cards, pick the one that's expiring soon.
  const cardTokenWithReplacementDueDate = cards.map(c => {
    const repDueDate = c.orderedCard?.cardReplacement?.replacementDueDate;
    return {
      token: c.orderedCard?.cardToken,
      due: repDueDate ? convertYearMonthDayToDate(repDueDate) : undefined
    };
  }).filter(c => Boolean(c.due && c.token)).sort((a, b) => a.due.getTime() - b.due.getTime())?.[0]?.token ?? null;
  return [{
    ...state,
    cards,
    cardTokenWithReplacementDueDate,
    canInviteOrOrderNewCard: allowOrderNewCard,
    tooltipText: informationBannerText
  }, ActionCreator.requestCardRelationships];
};
const handleCardListRejected = (context, state, action) => {
  const {
    error
  } = action;
  context.sentry.captureException(error);
  return [{
    ...state,
    hasError: true,
    isLoading: false
  }, null];
};
const handleCardRelationshipsReceived = (state, action) => {
  const {
    responsesMap
  } = action;
  const isOwnerCardMap = Object.entries(responsesMap).reduce((acc, [cardToken, response]) => {
    acc[cardToken] = response.relationships.includes(RetrieveCardRelationshipsResponse.Relationship.RELATIONSHIP_CARD_HOLDER);
    return acc;
  }, {});
  return [{
    ...state,
    /**
     * If owner does not have any cards, the map will only have false values. So
     * looking for the first instance of true will mean they have a non-terminated
     * card.
     */
    doesOwnerHaveNonTerminatedCard: Object.values(isOwnerCardMap).some(Boolean),
    hasError: false,
    isLoading: false
  }, null];
};
const handleCardRelationshipsRejected = (context, state, action) => {
  const {
    error
  } = action;
  context.sentry.captureException(error);
  return [{
    ...state,
    hasError: true,
    isLoading: false
  }, null];
};
const handleRequestCardList = (context, state) => {
  if (state.unitToken.length <= 0) {
    context.sentry.captureException('workflow/balance/card-list - Unit token is empty');
    return [{
      ...state,
      hasError: true,
      isLoading: false
    }, null];
  }
  const work = () => context.getCardList(GetCardListRequest.create({
    unitToken: state.unitToken
  })).then(ActionCreator.cardListReceived, ActionCreator.cardListRejected);
  return [{
    ...state,
    isLoading: true
  }, work];
};

/**
 * The reason why we are fetching the relationship for each card instead of
 * adding it into the bb-frontend API is because all this is temporary.
 *
 * TODO(BANKWEB-1069): When new card management is support owner cards, we will remove these
 * relationships calls.
 */
const handleRequestCardRelationships = (context, state) => {
  const {
    cards
  } = state;
  try {
    const promiseEntries = cards.reduce((acc, card) => {
      if (card.orderedCard) {
        const {
          cardToken
        } = card.orderedCard;
        assert(cardToken.length > 0, 'workflow/balance/card-list - card token from ordered card is empty');
        acc[0].push(cardToken);
        acc[1].push(context.retrieveCardRelationships(RetrieveCardRelationshipsRequest.create({
          cardToken
        })));
      }
      return acc;
    }, [[], []]);
    const work = () => {
      const [keys, values] = promiseEntries;
      return Promise.all(values).then(responses => {
        const map = Object.fromEntries(keys.map((key, index) => [key, responses[index]]));
        return ActionCreator.cardRelationshipsReceived(map);
      }, ActionCreator.cardRelationshipsRejected);
    };
    return [state, work];
  } catch (error) {
    context.sentry.captureException(error);
    return [{
      ...state,
      hasError: true,
      isLoading: false
    }, null];
  }
};
export const update = context => (state, action) => {
  switch (action.type) {
    case ActionType.CardListReceived:
      return handleCardListReceived(state, action);
    case ActionType.CardListRejected:
      return handleCardListRejected(context, state, action);
    case ActionType.CardRelationshipsReceived:
      return handleCardRelationshipsReceived(state, action);
    case ActionType.CardRelationshipsRejected:
      return handleCardRelationshipsRejected(context, state, action);
    case ActionType.RequestCardList:
      return handleRequestCardList(context, state);
    case ActionType.RequestCardRelationships:
      return handleRequestCardRelationships(context, state);
    case ActionType.UserClickAddDebitCard:
      return [state, null];
  }
};